/**
 * ProjectPopup Component
 * Clean modal popup for displaying project details with proper scrolling
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { useSafeThemeClasses } from '@/shared/theme';
import { Project } from '@/shared/types';

interface ProjectPopupProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project | null;
}

export const ProjectPopup: React.FC<ProjectPopupProps> = ({ isOpen, onClose, project }) => {
  const theme = useSafeThemeClasses();
  const [mounted, setMounted] = React.useState(false);

  // Handle modal opening/closing (delay unmount for exit animation)
  React.useEffect(() => {
    if (isOpen) {
      setMounted(true);
    } else if (mounted) {
      const timer = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, mounted]);

  // Remove the cleanup effect that was locking scroll
  // We want to allow page scrolling when cursor is outside modal

  // Handle escape key
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!mounted || !project) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Prevent page scroll when scrolling inside modal content
  const handleModalWheel = (e: React.WheelEvent) => {
    const target = e.currentTarget as HTMLElement;
    const { scrollTop, scrollHeight, clientHeight } = target;
    
    // Allow modal to scroll if it has scrollable content
    if (scrollHeight > clientHeight) {
      // Check if we're at the scroll boundaries
      const atTop = scrollTop === 0 && e.deltaY < 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight && e.deltaY > 0;
      
      // Only prevent default if we're not at boundaries (to allow modal scrolling)
      if (!atTop && !atBottom) {
        e.stopPropagation();
        return;
      }
    }
    
    // At boundaries or no scroll needed, allow but stop propagation to prevent page scroll
    e.stopPropagation();
  };

  const modal = (
    <div
      className={`fixed inset-0 z-[100] transition-opacity duration-300 ${
        isOpen ? 'opacity-100' : 'opacity-0'
      } ${theme.modalOverlay}`}
      onClick={handleBackdropClick}
      style={{
        pointerEvents: isOpen ? 'auto' : 'none',
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="project-popup-title"
    >
      <div
        className="flex min-h-full w-full items-center justify-center p-2 pt-4 sm:p-4 sm:pt-8"
        style={{ pointerEvents: 'none' }}
      >
          <div
            className={`relative w-full max-w-4xl transform transition-all duration-300 modal-content-container ${
              isOpen
                ? 'opacity-100 scale-100 translate-y-0'
                : 'opacity-0 scale-95 translate-y-4'
            }`}
            style={{ pointerEvents: 'auto' }}
          >
            {/* Modal Content */}
            <div
              className={`relative ${theme.modalContent} rounded-2xl shadow-2xl max-h-[95vh] overflow-hidden`}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className={`absolute top-4 right-4 z-10 p-2 rounded-full ${theme.buttonGhost} hover:scale-110 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2`}
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Scrollable Content */}
              <div 
                className="overflow-y-auto max-h-[95vh] modal-content glass-scrollbar"
                onWheel={handleModalWheel}
              >
                {/* Project Image */}
                <div className={`relative aspect-video overflow-hidden ${theme.card}`}>
                  <img
                    src={project.image ?? 'https://placehold.co/1200x600?text=Project+Image'}
                    alt={project.title}
                    className="w-full h-full object-contain"
                    style={{ objectPosition: 'center' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>

                {/* Content */}
                <div className="p-4 md:p-6 lg:p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2">
                      <h1
                        id="project-popup-title"
                        className={`text-3xl md:text-4xl font-bold mb-4 ${theme.textHeading}`}
                      >
                        {project.title}
                      </h1>
                      
                      <p className={`text-lg mb-8 ${theme.textSecondary}`}>
                        {project.description}
                      </p>

                      <div className="space-y-8">
                        {project.details?.overview && (
                          <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${theme.textHeading}`}>
                              Project Overview
                            </h2>
                            <div className={`${theme.textPrimary} leading-relaxed space-y-3`}>
                              {project.details.overview.map((paragraph, index) => (
                                <p key={`overview-${index}`}>{paragraph}</p>
                              ))}
                            </div>
                          </div>
                        )}

                        {project.details?.solution && (
                          <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${theme.textHeading}`}>
                              Solution & Implementation
                            </h2>
                            <ul className={`space-y-3 ${theme.textPrimary}`}>
                              {project.details.solution.map((item, index) => (
                                <li key={`solution-${index}`} className="flex items-start gap-3">
                                  <span className={`text-sm mt-1.5 ${theme.textAccent}`}>•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {project.details?.results && (
                          <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${theme.textHeading}`}>
                              Results & Impact
                            </h2>
                            <ul className={`space-y-3 ${theme.textPrimary}`}>
                              {project.details.results.map((item, index) => (
                                <li key={`results-${index}`} className="flex items-start gap-3">
                                  <span className={`text-sm mt-1.5 ${theme.textAccent}`}>•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {project.tags && project.tags.length > 0 && (
                          <div>
                            <h2 className={`text-2xl font-semibold mb-4 ${theme.textHeading}`}>
                              Technologies Used
                            </h2>
                            <div className="flex flex-wrap gap-3">
                              {project.tags.map((tag, index) => (
                                <span
                                  key={`popup-tag-${tag}-${index}`}
                                  className={`px-4 py-2 rounded-full text-sm font-medium ${theme.badge}`}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-1">
                      <div className={`p-6 rounded-xl ${theme.card} space-y-6`}>
                        <h3 className={`text-xl font-semibold ${theme.textHeading}`}>
                          Project Details
                        </h3>
                        
                        <div className="space-y-4">
                          {project.client && (
                            <div>
                              <div className={`text-sm font-medium ${theme.textMuted} mb-1`}>
                                Client
                              </div>
                              <div className={`font-medium ${theme.textPrimary}`}>{project.client}</div>
                            </div>
                          )}
                          
                          <div>
                            <div className={`text-sm font-medium ${theme.textMuted} mb-1`}>
                              Status
                            </div>
                            <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${theme.badge}`}>
                              {project.status === 'live' ? 'LIVE' : project.status === 'inactive' ? 'INACTIVE' : project.status?.toUpperCase() || 'ONGOING'}
                            </div>
                          </div>
                          
                          {project.completionDate && (
                            <div>
                              <div className={`text-sm font-medium ${theme.textMuted} mb-1`}>
                                {project.status === 'live' ? 'Status' : 'Completion Date'}
                              </div>
                              <div className={theme.textPrimary}>{project.completionDate}</div>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="space-y-3 pt-4">
                          {project.status === 'live' && project.liveUrl && (
                            <a
                              href={project.liveUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 ${theme.buttonPrimary} ${theme.buttonTextPrimary} hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 text-center block`}
                            >
                              Official Page
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ProjectPopup;
